import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeam } from '@/hooks/useTeam';
import { usePipelines } from '@/hooks/usePipelines';
import { useLossReasons } from '@/hooks/useLossReasons';
import { Loader as Loader2 } from 'lucide-react';
import type { ContactOpportunity } from '@/hooks/useContactDetails';

export interface OpportunityEditData {
  title: string;
  pipeline_id: string;
  stage_id: string;
  product?: string;
  price?: number;
  status: 'open' | 'won' | 'lost';
  closed_at?: string;
  sale_date?: string;
  won_by_user_id?: string;
  loss_reason_id?: string;
  notes?: string;
}

interface EditOpportunityFullDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: OpportunityEditData) => Promise<void>;
  opportunity?: ContactOpportunity | null;
}

export function EditOpportunityFullDialog({
  open,
  onClose,
  onSave,
  opportunity,
}: EditOpportunityFullDialogProps) {
  const { members, loading: loadingMembers } = useTeam();
  const { pipelines, pipelineStages } = usePipelines();
  const { lossReasons, loading: loadingReasons } = useLossReasons();

  const [title, setTitle] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [product, setProduct] = useState('');
  const [price, setPrice] = useState('');
  const [closedAt, setClosedAt] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [wonByUserId, setWonByUserId] = useState('');
  const [lossReasonId, setLossReasonId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const derivedStatus: 'open' | 'won' | 'lost' = (() => {
    if (!stageId) return 'open';
    const stage = pipelineStages.find(s => s.id === stageId);
    if (!stage) return 'open';
    if (stage.stage_type === 'won') return 'won';
    if (stage.stage_type === 'lost') return 'lost';
    return 'open';
  })();

  useEffect(() => {
    if (open && opportunity) {
      setTitle(opportunity.title || '');
      setPipelineId(opportunity.pipeline_id || '');
      setStageId(opportunity.pipeline_stage_id || '');
      setProduct(opportunity.product || '');
      setPrice(opportunity.price ? String(opportunity.price) : '');
      setClosedAt(opportunity.closed_at ? new Date(opportunity.closed_at).toISOString().split('T')[0] : '');
      setSaleDate(opportunity.sale_date ? new Date(opportunity.sale_date).toISOString().split('T')[0] : '');
      setWonByUserId(opportunity.won_by_user_id || '');
      setLossReasonId(opportunity.loss_reason_id || '');
      setNotes(opportunity.notes || '');
    }
  }, [open, opportunity]);

  const filteredStages = pipelineId
    ? pipelineStages.filter((s) => s.pipeline_id === pipelineId)
    : [];

  const activeMembers = members.filter((m) => m.is_active);

  const handlePipelineChange = (newPipelineId: string) => {
    setPipelineId(newPipelineId);
    const stages = pipelineStages.filter((s) => s.pipeline_id === newPipelineId);
    if (stages.length > 0) {
      setStageId(stages[0].id);
    } else {
      setStageId('');
    }
  };

  const handleStageChange = (newStageId: string) => {
    setStageId(newStageId);
    const stage = pipelineStages.find(s => s.id === newStageId);
    if (!stage || stage.stage_type === 'normal') {
      setClosedAt('');
      setSaleDate('');
      setWonByUserId('');
      setLossReasonId('');
    } else if (stage.stage_type === 'won') {
      setLossReasonId('');
      if (!closedAt) {
        setClosedAt(new Date().toISOString().split('T')[0]);
      }
    } else if (stage.stage_type === 'lost') {
      setSaleDate('');
      setWonByUserId('');
      if (!closedAt) {
        setClosedAt(new Date().toISOString().split('T')[0]);
      }
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !pipelineId || !stageId) return;

    setIsSubmitting(true);

    const data: OpportunityEditData = {
      title: title.trim(),
      pipeline_id: pipelineId,
      stage_id: stageId,
      product: product.trim() || undefined,
      price: price ? parseFloat(price) : undefined,
      status: derivedStatus,
      closed_at: closedAt ? new Date(closedAt).toISOString() : undefined,
      sale_date: derivedStatus === 'won' && saleDate ? new Date(saleDate).toISOString() : undefined,
      won_by_user_id: derivedStatus === 'won' && wonByUserId ? wonByUserId : undefined,
      loss_reason_id: derivedStatus === 'lost' && lossReasonId ? lossReasonId : undefined,
      notes: notes.trim() || undefined,
    };

    try {
      await onSave(data);
      handleClose();
    } catch (error) {
      console.error('Error saving opportunity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setPipelineId('');
    setStageId('');
    setProduct('');
    setPrice('');
    setClosedAt('');
    setSaleDate('');
    setWonByUserId('');
    setLossReasonId('');
    setNotes('');
    onClose();
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    if (!rawValue) {
      setPrice('');
      return;
    }
    const numericValue = parseInt(rawValue, 10) / 100;
    setPrice(String(numericValue));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#18181B] border-[#3F3F464D] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Editar Oportunidade</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-white">Titulo *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titulo da oportunidade"
              className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-white">Pipeline *</Label>
              <Select value={pipelineId} onValueChange={handlePipelineChange}>
                <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {pipelines.map((pipeline) => (
                      <SelectItem key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Etapa *</Label>
              <Select value={stageId} onValueChange={handleStageChange} disabled={!pipelineId}>
                <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {filteredStages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Produto/Servico</Label>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="Nome do produto ou servico"
              className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Valor (R$)</Label>
            <Input
              value={price ? formatCurrency(String(Math.round(parseFloat(price) * 100))) : ''}
              onChange={handlePriceChange}
              placeholder="0,00"
              className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
            />
          </div>

          {(derivedStatus === 'won' || derivedStatus === 'lost') && (
            <div className="space-y-2">
              <Label className="text-white">Data de Fechamento</Label>
              <Input
                type="date"
                value={closedAt}
                onChange={(e) => setClosedAt(e.target.value)}
                className="bg-neutral-600 text-white border-[#3F3F46] [color-scheme:dark]"
              />
            </div>
          )}

          {derivedStatus === 'won' && (
            <>
              <div className="space-y-2">
                <Label className="text-white">Data da Venda</Label>
                <Input
                  type="date"
                  value={saleDate}
                  onChange={(e) => setSaleDate(e.target.value)}
                  className="bg-neutral-600 text-white border-[#3F3F46] [color-scheme:dark]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Responsavel pela Venda</Label>
                <Select value={wonByUserId || 'none'} onValueChange={(v) => setWonByUserId(v === 'none' ? '' : v)} disabled={loadingMembers}>
                  <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                    <SelectValue placeholder={loadingMembers ? 'Carregando...' : 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {derivedStatus === 'lost' && (
            <div className="space-y-2">
              <Label className="text-white">Motivo da Perda</Label>
              <Select value={lossReasonId || 'none'} onValueChange={(v) => setLossReasonId(v === 'none' ? '' : v)} disabled={loadingReasons}>
                <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                  <SelectValue placeholder={loadingReasons ? 'Carregando...' : 'Selecione o motivo'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {lossReasons.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-white">Anotacoes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observacoes sobre a oportunidade..."
              className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400 min-h-[80px] resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-white text-white hover:bg-white hover:text-black"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting || !title.trim() || !pipelineId || !stageId}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
